import { useEffect, useState } from "react";
import type { ColumnsType } from "antd/es/table";
import { Segmented, Tag } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { uniqBy } from "lodash";
import TableList from "../../ui-component/Table";
import { Header } from "../../components/Header";
import { useGetData } from "../../Network/useGetQueryData";
import "./style.scss";

type CodeType = "phone" | "otp" | "membership" | "password" | "emailCode";

interface SentCodesStats {
  phone: { total: number; verified: number; pending: number };
  otp: { total: number; used: number; expired: number; active: number };
  membership: {
    total: number;
    redeemed: number;
    accepted: number;
    pending: number;
    rejected: number;
    cancelled: number;
    expired: number;
  };
  passwordReset: { total: number };
  emailVerification: { total: number; active: number; expired: number };
}

interface EmailVerificationRow {
  id: number;
  firstName: string;
  lastName: string | null;
  email: string;
  phoneNumber: string;
  countryCode: string;
  emailVerificationCode: string;
  emailVerificationCodeCreatedAt: string;
  emailVerified: boolean;
}

interface PasswordResetRow {
  id: number;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phoneNumber: string;
  countryCode: string;
  resetPasswordToken: string;
  resetPasswordTokenCreatedAt: string;
}

interface PhoneNumberRow {
  id: number;
  phoneNumber: string;
  countryCode: string;
  verificationCode: string;
  isVerified: boolean;
  userId: number | null;
  createdAt: string;
  user?: { firstName?: string; lastName?: string; email?: string };
}

interface CustomerLike {
  contactDetails?: any;
  user?: { firstName?: string; lastName?: string; email?: string; phoneNumber?: string };
}

interface CustomerOtpRow {
  id: number;
  otp: number;
  isUsed: boolean;
  expiresAt: string;
  createdAt: string;
  customer?: CustomerLike;
  serviceProvider?: { name?: { en?: string; ar?: string } };
}

interface MembershipRequestRow {
  id: number;
  redeemCode?: string;
  status: string;
  customerId?: number;
  sponsorType?: string;
  createdAt: string;
  updatedAt: string;
  customer?: CustomerLike;
  membershipPlan?: { name?: { en?: string; ar?: string } };
  sponsor?: { name?: { en?: string; ar?: string } };
}

const getCustomerContact = (customer?: CustomerLike) => {
  let contact: Record<string, any> = {};
  if (customer?.contactDetails) {
    try {
      contact =
        typeof customer.contactDetails === "string"
          ? JSON.parse(customer.contactDetails)
          : customer.contactDetails;
    } catch {
      contact = {};
    }
  }
  return {
    name: customer?.user?.firstName || contact.firstName || customer?.user?.phoneNumber || contact.phoneNumber || "-",
    email: customer?.user?.email || contact.email || "-",
  };
};

const SentCodesPage = () => {
  const { t, i18n } = useTranslation();
  const [codeType, setCodeType] = useState<CodeType>("phone");
  const [offset, setOffset] = useState(0);
  const [searchKey, setSearchKey] = useState<string>();
  const [rows, setRows] = useState<any[]>([]);
  const [limit] = useState(10);

  const urlByType: Record<CodeType, string> = {
    phone: "/phone-number",
    otp: "/customer/otp",
    membership: "/membership-request",
    password: "/report/password-reset-requests",
    emailCode: "/report/email-verification-requests",
  };

  const sortKeyByType: Record<CodeType, string> = {
    phone: "createdAt:-1",
    otp: "createdAt:-1",
    membership: "updatedAt:-1",
    password: "",
    emailCode: "",
  };

  const { data: statsData } = useGetData<{ data: SentCodesStats }>({
    queryKey: ["sent-codes-stats"],
    url: "/report/sent-codes-stats",
  });
  const stats = statsData?.data;

  const { data, isLoading } = useGetData<{
    data: { items: any[]; count: number };
  }>({
    queryKey: ["sent-codes", codeType, { offset, limit, searchKey }],
    url: urlByType[codeType],
    params:
      codeType === "phone"
        ? { offset, limit, searchKey, withDeleted: false, sortKey: sortKeyByType[codeType] }
        : { offset, limit, sortKey: sortKeyByType[codeType] },
  });

  useEffect(() => {
    setRows([]);
    setOffset(0);
  }, [codeType]);

  useEffect(() => {
    if (data?.data?.items && !!searchKey) {
      setRows(data.data.items);
      return;
    }
    if (data?.data?.items) {
      setRows((prev) => [...prev, ...data.data.items]);
    }
  }, [data]);

  const loadMoreItems = () => {
    if (data && rows.length < data.data.count && !isLoading) {
      setOffset((prev) => prev + 1);
    }
  };

  const phoneColumns: ColumnsType<PhoneNumberRow> = [
    {
      title: t("sentCodes.table.recipient"),
      key: "phone",
      render: (_, record) => (
        <p>
          {record.countryCode}
          {record.phoneNumber}
        </p>
      ),
    },
    {
      title: t("sentCodes.table.code"),
      key: "code",
      render: (_, record) => <p>{record.verificationCode}</p>,
    },
    {
      title: t("sentCodes.table.status"),
      key: "status",
      render: (_, record) =>
        record.isVerified ? (
          <Tag color="success">{t("sentCodes.status.verified")}</Tag>
        ) : (
          <Tag color="warning">{t("sentCodes.status.pending")}</Tag>
        ),
    },
    {
      title: t("sentCodes.table.customer"),
      key: "customer",
      render: (_, record) =>
        record.user ? (
          <p>
            {record.user.firstName} {record.user.lastName}
          </p>
        ) : (
          <p>-</p>
        ),
    },
    {
      title: t("sentCodes.table.email"),
      key: "email",
      render: (_, record) => <p>{record.user?.email || "-"}</p>,
    },
    {
      title: t("sentCodes.table.sentAt"),
      key: "createdAt",
      render: (_, record) => <p>{dayjs(record.createdAt).format("DD/MM/YYYY HH:mm")}</p>,
    },
  ];

  const otpColumns: ColumnsType<CustomerOtpRow> = [
    {
      title: t("sentCodes.table.customer"),
      key: "customer",
      render: (_, record) => <p>{getCustomerContact(record.customer).name}</p>,
    },
    {
      title: t("sentCodes.table.email"),
      key: "email",
      render: (_, record) => <p>{getCustomerContact(record.customer).email}</p>,
    },
    {
      title: t("sentCodes.table.provider"),
      key: "provider",
      render: (_, record) => (
        <p>{record.serviceProvider?.name?.[i18n.language] || record.serviceProvider?.name?.en}</p>
      ),
    },
    {
      title: t("sentCodes.table.code"),
      key: "otp",
      render: (_, record) => <p>{record.otp}</p>,
    },
    {
      title: t("sentCodes.table.status"),
      key: "status",
      render: (_, record) => {
        if (record.isUsed) return <Tag color="success">{t("sentCodes.status.used")}</Tag>;
        if (dayjs(record.expiresAt).isBefore(dayjs()))
          return <Tag color="error">{t("sentCodes.status.expired")}</Tag>;
        return <Tag color="processing">{t("sentCodes.status.active")}</Tag>;
      },
    },
    {
      title: t("sentCodes.table.sentAt"),
      key: "createdAt",
      render: (_, record) => <p>{dayjs(record.createdAt).format("DD/MM/YYYY HH:mm")}</p>,
    },
  ];

  const membershipStatusColor: Record<string, string> = {
    redeemed: "success",
    accepted: "processing",
    pending: "warning",
    rejected: "error",
    cancelled: "default",
    expired: "error",
  };

  const membershipColumns: ColumnsType<MembershipRequestRow> = [
    {
      title: t("sentCodes.table.code"),
      key: "redeemCode",
      render: (_, record) => <p>{record.redeemCode || "-"}</p>,
    },
    {
      title: t("sentCodes.table.customer"),
      key: "customer",
      render: (_, record) => <p>{getCustomerContact(record.customer).name}</p>,
    },
    {
      title: t("sentCodes.table.email"),
      key: "email",
      render: (_, record) => <p>{getCustomerContact(record.customer).email}</p>,
    },
    {
      title: t("sentCodes.table.membershipPlan"),
      key: "membershipPlan",
      render: (_, record) => (
        <p>{record.membershipPlan?.name?.[i18n.language] || record.membershipPlan?.name?.en}</p>
      ),
    },
    {
      title: t("sentCodes.table.sponsor"),
      key: "sponsor",
      render: (_, record) => (
        <p>{record.sponsor?.name?.[i18n.language] || record.sponsor?.name?.en || "-"}</p>
      ),
    },
    {
      title: t("sentCodes.table.status"),
      key: "status",
      render: (_, record) => (
        <Tag color={membershipStatusColor[record.status] || "default"}>
          {t(`sentCodes.membershipStatus.${record.status}`, record.status)}
        </Tag>
      ),
    },
    {
      title: t("sentCodes.table.sentAt"),
      key: "updatedAt",
      render: (_, record) => <p>{dayjs(record.updatedAt).format("DD/MM/YYYY HH:mm")}</p>,
    },
  ];

  const passwordColumns: ColumnsType<PasswordResetRow> = [
    {
      title: t("sentCodes.table.customer"),
      key: "customer",
      render: (_, record) => (
        <p>
          {record.firstName} {record.lastName}
        </p>
      ),
    },
    {
      title: t("sentCodes.table.email"),
      key: "email",
      render: (_, record) => <p>{record.email || "-"}</p>,
    },
    {
      title: t("sentCodes.table.recipient"),
      key: "phone",
      render: (_, record) => (
        <p>
          {record.countryCode}
          {record.phoneNumber}
        </p>
      ),
    },
    {
      title: t("sentCodes.table.code"),
      key: "code",
      render: (_, record) => <p>{record.resetPasswordToken}</p>,
    },
    {
      title: t("sentCodes.table.status"),
      key: "status",
      render: () => <Tag color="warning">{t("sentCodes.status.pending")}</Tag>,
    },
    {
      title: t("sentCodes.table.sentAt"),
      key: "resetPasswordTokenCreatedAt",
      render: (_, record) => (
        <p>{dayjs(record.resetPasswordTokenCreatedAt).format("DD/MM/YYYY HH:mm")}</p>
      ),
    },
  ];

  const emailCodeColumns: ColumnsType<EmailVerificationRow> = [
    {
      title: t("sentCodes.table.customer"),
      key: "customer",
      render: (_, record) => (
        <p>
          {record.firstName} {record.lastName}
        </p>
      ),
    },
    {
      title: t("sentCodes.table.email"),
      key: "email",
      render: (_, record) => <p>{record.email}</p>,
    },
    {
      title: t("sentCodes.table.code"),
      key: "code",
      render: (_, record) => <p>{record.emailVerificationCode}</p>,
    },
    {
      title: t("sentCodes.table.status"),
      key: "status",
      render: (_, record) => {
        if (record.emailVerified)
          return <Tag color="success">{t("sentCodes.status.verified")}</Tag>;
        // Codes are good for 24 hours from the moment they are issued.
        const expired = dayjs(record.emailVerificationCodeCreatedAt).isBefore(
          dayjs().subtract(24, "hour")
        );
        return expired ? (
          <Tag color="error">{t("sentCodes.status.expired")}</Tag>
        ) : (
          <Tag color="processing">{t("sentCodes.status.active")}</Tag>
        );
      },
    },
    {
      title: t("sentCodes.table.sentAt"),
      key: "emailVerificationCodeCreatedAt",
      render: (_, record) => (
        <p>
          {dayjs(record.emailVerificationCodeCreatedAt).format(
            "DD/MM/YYYY HH:mm"
          )}
        </p>
      ),
    },
  ];

  const columnsByType: Record<CodeType, ColumnsType<any>> = {
    phone: phoneColumns,
    otp: otpColumns,
    membership: membershipColumns,
    password: passwordColumns,
    emailCode: emailCodeColumns,
  };

  const statCards: { label: string; value: number }[] = (() => {
    if (!stats) return [];
    if (codeType === "phone") {
      return [
        { label: t("sentCodes.stats.total"), value: stats.phone.total },
        { label: t("sentCodes.status.verified"), value: stats.phone.verified },
        { label: t("sentCodes.status.pending"), value: stats.phone.pending },
      ];
    }
    if (codeType === "otp") {
      return [
        { label: t("sentCodes.stats.total"), value: stats.otp.total },
        { label: t("sentCodes.status.used"), value: stats.otp.used },
        { label: t("sentCodes.status.active"), value: stats.otp.active },
        { label: t("sentCodes.status.expired"), value: stats.otp.expired },
      ];
    }
    if (codeType === "password") {
      return [{ label: t("sentCodes.stats.total"), value: stats.passwordReset.total }];
    }
    if (codeType === "emailCode") {
      if (!stats.emailVerification) return [];
      return [
        { label: t("sentCodes.stats.total"), value: stats.emailVerification.total },
        { label: t("sentCodes.status.active"), value: stats.emailVerification.active },
        { label: t("sentCodes.status.expired"), value: stats.emailVerification.expired },
      ];
    }
    return [
      { label: t("sentCodes.stats.total"), value: stats.membership.total },
      { label: t("sentCodes.membershipStatus.redeemed"), value: stats.membership.redeemed },
      { label: t("sentCodes.membershipStatus.accepted"), value: stats.membership.accepted },
      { label: t("sentCodes.membershipStatus.pending"), value: stats.membership.pending },
      { label: t("sentCodes.membershipStatus.rejected"), value: stats.membership.rejected },
      { label: t("sentCodes.membershipStatus.expired"), value: stats.membership.expired },
    ];
  })();

  return (
    <>
      <Header
        title={t("sentCodes.header.title")}
        headerFilter={
          <Segmented
            value={codeType}
            onChange={(value) => setCodeType(value as CodeType)}
            options={[
              { label: t("sentCodes.type.phone"), value: "phone" },
              { label: t("sentCodes.type.otp"), value: "otp" },
              { label: t("sentCodes.type.membership"), value: "membership" },
              { label: t("sentCodes.type.password"), value: "password" },
              { label: t("sentCodes.type.emailCode"), value: "emailCode" },
            ]}
          />
        }
        search={
          codeType === "phone"
            ? {
                placeholder: t("sentCodes.search.placeholder"),
                searchAction: (val) => {
                  offset > 0 && setOffset(0);
                  setTimeout(() => setSearchKey(val), 300);
                },
              }
            : undefined
        }
      />
      {statCards.length > 0 && (
        <div className="sent-codes-stats-grid">
          {statCards.map((card) => (
            <div className="sent-codes-stat-card" key={card.label}>
              <h3>{card.value}</h3>
              <p>{card.label}</p>
            </div>
          ))}
        </div>
      )}
      <TableList
        data={uniqBy(rows, "id")}
        columns={columnsByType[codeType]}
        loading={searchKey && isLoading}
        rowKey="id"
        loadMore={loadMoreItems}
        isLoadMore={isLoading}
      />
    </>
  );
};

export default SentCodesPage;
